import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { isAddressBlank } from '@/lib/address'
import { geocodeAddress } from '@/lib/geocoding'
import { DEFAULT_CIRCLE_RADIUS } from '@/lib/locations'
import { performMutations, useMutation } from '@/lib/mutations'
import { useStore } from '@/lib/pocketbase'
import type { Addresses } from '@/types/pbSchema'

interface UseJobOptions {
    enabled?: boolean
}

export function useJob(jobSlug?: string | null, { enabled = true }: UseJobOptions = {}) {
    const [jobsCollection, addressesCollection] = useStore('jobs', 'addresses')

    const { data: rows = [], isLoading } = useLiveQuery(
        q =>
            jobSlug && enabled
                ? q
                      .from({ job: jobsCollection })
                      .where(({ job }) => eq(job.identifier, jobSlug))
                      .join(
                          { address: addressesCollection },
                          ({ job, address }) => eq(job.address, address.id),
                          'left'
                      )
                : undefined,
        [jobSlug, enabled]
    )

    // Rehydrate `job.expand.address` from the joined row so consumers
    // (JobCard, JobFormFields, etc.) keep working unchanged. pbtsdb 0.4+
    // doesn't refetch on mutation, so realtime SSE events deliver job records
    // without expand data.
    const first = rows[0]
    const job = first?.job
        ? ({
              ...first.job,
              expand: { ...(first.job as { expand?: object }).expand, address: first.address },
          } as typeof first.job)
        : null

    return {
        job,
        data: job,
        isLoading,
        isPending: isLoading,
        isError: false,
        error: null,
    }
}

export function useDeleteJob() {
    const [jobsCollection] = useStore('jobs')

    const mutation = useMutation({
        mutationFn: async (jobId: string) => {
            await performMutations(function* () {
                yield jobsCollection.delete(jobId)
            })
        },
    })

    return {
        deleteJob: mutation.mutate,
        isLoading: mutation.isPending,
        mutation,
    }
}

export function useJobHasTimeEntries(jobId?: string) {
    const [timeEntriesCollection] = useStore('time_entries')

    const { data: entries = [], isLoading } = useLiveQuery(
        q =>
            jobId
                ? q
                      .from({ time_entries: timeEntriesCollection })
                      .where(({ time_entries }) => eq(time_entries.job, jobId))
                      .orderBy(({ time_entries }) => time_entries.id, 'asc')
                      .limit(1)
                : undefined,
        [jobId]
    )

    return {
        hasTimeEntries: entries.length > 0,
        isLoading,
    }
}

export async function autoLocationForJobAddress(address: Omit<Addresses, 'id'>) {
    if (isAddressBlank(address) && address.city) {
        return null
    }

    const geocodeResult = await geocodeAddress(address)
    if (geocodeResult) {
        return {
            description: geocodeResult.formattedAddress,
            geometry: {
                type: 'Point' as const,
                coordinates: [geocodeResult.longitude, geocodeResult.latitude] as [number, number],
                radius: DEFAULT_CIRCLE_RADIUS,
            },
        }
    }

    return null
}
